import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import * as React from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteShell } from "@/components/SiteShell";
import { ThankYouVideoPlayer } from "@/components/order/ThankYouVideoPlayer";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useTrackOrderAttemptStore } from "@/stores/trackOrderAttemptStore";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Loader2, CheckCircle2, Truck, Clock, AlertCircle, Share2, ChevronRight, RotateCcw, Copy, Check, ExternalLink, ShoppingBag, PackageCheck, RefreshCw, MessageCircle, XCircle, Bell, BellRing } from "lucide-react";
import { optStr, optBool, optEnum } from "@/lib/search-validators";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";
// parsePhoneNumberFromString لا يُستخدم في النموذج الرئيسيّ بعد إزالة حقل الموبايل.
// التطبيع للموبايل يجري الآن سيرفر-سايد فقط (في /api/track-order).
import { toast } from "@/lib/toast";
import { getMyOrders, type MyOrderSummary } from "@/utils/orders.functions";
import { TrackOtpPanel } from "@/components/order/TrackOtpPanel";
import {
  buildArabicMessage,
  buildEnglishMessage,
  buildFrenchMessage,
  getStatusEmoji,
} from "@/lib/whatsappMessages";
import { usePollingRefresh } from "@/hooks/usePollingRefresh";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";

export const Route = createFileRoute("/track-order")({
  component: TrackOrderPage,
  validateSearch: (search: Record<string, unknown>): {
    order?: string; email?: string; phone?: string; last4?: string;
    auto?: boolean; ctx?: "empty"; t?: string;
  } => ({
    order: optStr(search.order),
    email: optStr(search.email),
    phone: optStr(search.phone),
    last4: optStr(search.last4),
    auto: optBool(search.auto),
    ctx: optEnum(search.ctx, ["empty"] as const),
    /** توكن موقَّع من الإيميل/الواتساب — تتبّع فوريّ بلا نموذج */
    t: optStr(search.t),
  }),
  head: () => ({
    meta: [
      { title: "Track your order — Lecmo" },
      { name: "description", content: "Follow your Lecmo order in real time: confirmation, packing, dispatch, and delivery updates." },
      { property: "og:title", content: "Track your order — Lecmo" },
      { property: "og:description", content: "Enter the mobile number used for your order and we'll send you a verification code to see the live status of your Lecmo delivery." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/track-order` }],
  }),
});

const LAST_TRACK_KEY = "lecmo:lastTrack";
const LAST_TRACK_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FINAL_STATUSES = new Set(["delivered", "cancelled", "refunded"]);

type LastTrackPayload = {
  orderNumber?: string;
  email?: string;
  phone?: string;
  savedAt: number;
};

type TrackLookupPayload = { orderNumber?: string; email?: string; phone?: string; t?: string };

function readLastTrack(): LastTrackPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LAST_TRACK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastTrackPayload;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > LAST_TRACK_TTL_MS) {
      window.localStorage.removeItem(LAST_TRACK_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveLastTrack(p: Omit<LastTrackPayload, "savedAt">) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      LAST_TRACK_KEY,
      JSON.stringify({ ...p, savedAt: Date.now() }),
    );
  } catch {
    // ignore
  }
}

interface TrackResult {
  orderName: string;
  createdAt: string;
  financialStatus: string | null;
  cancelledAt?: string | null;
  fulfillmentStatus: string;
  tracking: {
    number: string | null;
    url: string | null;
    company: string | null;
    updatedAt: string | null;
    createdAt?: string | null;
    status: string | null;
    shipmentStatus?: string | null;
  } | null;
  items: Array<{ title: string; qty: number; image?: string | null }>;
  /** حالة سائقنا المحليّ (null = لا تسليم محليّ أو الميزة معطَّلة من الأدمن) */
  localDelivery?: {
    status: string;
    driverName: string | null;
    assignedAt: string | null;
    pickedUpAt: string | null;
    deliveredAt: string | null;
  } | null;
}

/** بصمة الحالة — أيّ تغيّر فيها يعني تقدّماً حقيقيّاً في الطلب. */
function statusKeyOf(r: TrackResult): string {
  return [
    r.financialStatus ?? "",
    r.fulfillmentStatus,
    r.localDelivery?.status ?? "",
    r.tracking?.number ?? "",
    r.tracking?.shipmentStatus ?? "",
  ].join("|");
}

/** تنبيه تلقائيّ عند تغيّر الحالة: toast دائماً + إشعار متصفّح إن كان مسموحاً. */
function notifyStatusChange(
  r: TrackResult,
  t: (en: string, ar: string, fr: string) => string,
) {
  const label = r.localDelivery?.status
    ? (localDeliveryLabel(r.localDelivery.status, t)?.label ?? r.fulfillmentStatus)
    : getStatusInfo(r.fulfillmentStatus, t).label;

  const title = t("Order status updated", "تحدّثت حالة طلبك", "Statut mis à jour");
  const body = `${r.orderName} — ${label}`;

  toast.success(title, { description: body, duration: 8000 });

  try {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body, icon: "/favicon.ico", tag: `track-${r.orderName}` });
    }
  } catch {
    // الإشعار رفاهيّة — لا نكسر الصفحة إن مُنع
  }
}

/** تسميات مراحل التسليم المحليّ (سائقنا) — تُعرَض فوق حالة Shopify. */
function localDeliveryLabel(
  status: string,
  t: (en: string, ar: string, fr: string) => string,
): { label: string; hint: string; emoji: string } | null {
  switch (status) {
    case "assigned":
      return {
        emoji: "📋",
        label: t("Assigned to a driver", "أُسنِد لسائق", "Assigné à un livreur"),
        hint: t(
          "Your order is with our delivery team.",
          "طلبك مع فريق التوصيل لدينا.",
          "Votre commande est avec notre équipe de livraison.",
        ),
      };
    case "picked_up":
      return {
        emoji: "📦",
        label: t("Picked up", "تمّ الاستلام من المستودع", "Récupérée"),
        hint: t(
          "The driver has collected your order.",
          "السائق استلم طلبك من المستودع.",
          "Le livreur a récupéré votre commande.",
        ),
      };
    case "out_for_delivery":
      return {
        emoji: "🛵",
        label: t("Driver on the way", "السائق في الطريق إليك", "Livreur en route"),
        hint: t(
          "Please be ready to receive your order.",
          "كن على الاستعداد لاستلام طلبك.",
          "Merci d'être prêt à recevoir votre commande.",
        ),
      };
    case "delivered":
      return {
        emoji: "✅",
        label: t("Delivered", "تمّ التسليم", "Livrée"),
        hint: t("Enjoy!", "نتمنّى أن يسعدك.", "Profitez-en !"),
      };
    case "failed":
      return {
        emoji: "⚠️",
        label: t("Delivery attempt failed", "تعذّر التسليم", "Échec de livraison"),
        hint: t(
          "We will contact you to reschedule.",
          "سنتواصل معك لإعادة الجدولة.",
          "Nous vous contacterons pour reprogrammer.",
        ),
      };
    case "returned":
      return {
        emoji: "↩️",
        label: t("Returned", "أُعيد الطلب", "Retournée"),
        hint: t(
          "The order returned to our warehouse.",
          "عاد الطلب إلى مستودعنا.",
          "La commande est revenue à notre entrepôt.",
        ),
      };
    default:
      return null;
  }
}



function TrackOrderPage() {
  const { locale, dir } = useLocale();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const search = Route.useSearch();
  const setAttempt = useTrackOrderAttemptStore((s) => s.setAttempt);

  const t = (en: string, ar: string, fr: string) =>
    locale === "ar" ? ar : locale === "fr" ? fr : en;

  const [orderNumber, setOrderNumber] = React.useState(search.order ?? "");
  const [email, setEmail] = React.useState(search.email ?? "");
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [result, setResult] = React.useState<TrackResult | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = React.useState<number | null>(null);
  // بصمة الحالة الأخيرة — لمقارنة التغيّر وإظهار تنبيه تلقائيّ
  const lastStatusKeyRef = React.useRef<string | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  // آخر payload ناجح — نستعمله للـ polling والـ refresh اليدوي
  const [lastPayload, setLastPayload] = React.useState<TrackLookupPayload | null>(null);
  // بانر «استئناف بحثك السابق»
  const [resumeSuggestion, setResumeSuggestion] = React.useState<LastTrackPayload | null>(null);

  // وضع البحث للضيوف: "with_id" (المعتاد) أو "forgot_id" (إيميل + آخر ٤)

  // طلباتي — يتم جلبها تلقائياً للمستخدم المسجَّل عبر بريده
  const [myOrders, setMyOrders] = React.useState<MyOrderSummary[] | null>(null);
  const [myOrdersLoading, setMyOrdersLoading] = React.useState(false);
  const [myOrdersError, setMyOrdersError] = React.useState(false);
  const fetchMyOrders = useServerFn(getMyOrders);

  const accountId = user?.id ?? null;
  const activeAccountRef = React.useRef(accountId);
  activeAccountRef.current = accountId;
  const previousAccountRef = React.useRef(accountId);
  const lookupGenerationRef = React.useRef(0);
  const activePayloadRef = React.useRef<TrackLookupPayload | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      lookupGenerationRef.current++;
      activePayloadRef.current = null;
    };
  }, []);

  // لا نصفّر الصفحة أثناء استرجاع الجلسة (null → id) — فقط عند تبديل حساب فعليّ.
  const authSettledRef = React.useRef(false);
  React.useEffect(() => {
    if (authLoading) return;
    if (!authSettledRef.current) {
      authSettledRef.current = true;
      previousAccountRef.current = accountId;
      return;
    }
    if (previousAccountRef.current === accountId) return;
    previousAccountRef.current = accountId;
    lookupGenerationRef.current++;
    activePayloadRef.current = null;
    lastStatusKeyRef.current = null;
    setResult(null);
    setLastPayload(null);
    setLastFetchedAt(null);
    setOrderNumber("");
    setEmail("");
    setLoading(false);
    setRefreshing(false);
    setErrorMsg(null);
    setResumeSuggestion(null);
    setMyOrders(null);
    setMyOrdersError(false);
    setMyOrdersLoading(false);
    setAttempt({});
    try { window.localStorage.removeItem(LAST_TRACK_KEY); } catch { /* Storage may be unavailable. */ }
  }, [accountId, authLoading, setAttempt]);

  // تعبئة البريد المسبقة من حساب المستخدم
  React.useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
  }, [user, email]);

  // جلب طلبات المستخدم تلقائياً
  React.useEffect(() => {
    if (!user) {
      setMyOrders(null);
      setMyOrdersError(false);
      return;
    }
    let cancelled = false;
    setMyOrdersLoading(true);
    setMyOrdersError(false);
    fetchMyOrders()
      .then((res) => {
        if (cancelled) return;
        setMyOrders(res.orders);
        if ((res as { error?: string }).error) {
          setMyOrdersError(true);
        }
      })
      .catch((err) => {
        console.error("[track-order] fetchMyOrders failed:", err);
        if (!cancelled) {
          setMyOrders([]);
          setMyOrdersError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setMyOrdersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, fetchMyOrders]);

  // ────────────────────────────────────────────────────
  // دالّة موحَّدة للجلب: تخدم الـ submit + الـ polling + الـ refresh اليدوي
  // ────────────────────────────────────────────────────
  const runTrack = React.useCallback(
    async (
      payload: TrackLookupPayload,
      opts: { silent?: boolean } = {},
    ) => {
      const silent = opts.silent ?? false;
      // Never start a lookup before the session is restored: the owner would be
      // captured as "guest" and the restored account would discard the result.
      if (authLoading) return null;
      const owner = user?.id ?? null;
      if (!mountedRef.current || owner !== activeAccountRef.current) return null;

      // A saved polling callback for an earlier order must not restart it.
      if (silent && activePayloadRef.current !== payload) return null;
      const generation = ++lookupGenerationRef.current;
      activePayloadRef.current = payload;
      const isCurrent = () => mountedRef.current && generation === lookupGenerationRef.current && owner === activeAccountRef.current;
      if (silent) setRefreshing(true);
      else {
        setLoading(true);
        setRefreshing(false);
        setErrorMsg(null);
        setResult(null);
        setLastPayload(null);
        setLastFetchedAt(null);
        lastStatusKeyRef.current = null;
      }
      // سجّل المحاولة قبل النداء — حتّى لو فشل، يلتقطها زرّ الواتساب
      if (!silent) {
        setAttempt({
          orderNumber: payload.orderNumber,
          email: payload.email,
          phoneLast4: payload.phone?.replace(/\D/g, "").slice(-4),
          errorCode: null,
        });
      }
      try {
        const res = await fetch("/api/track-order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!isCurrent()) return null;
        if (res.status === 429) {
          if (!silent) {
            setAttempt({
              orderNumber: payload.orderNumber,
              email: payload.email,
              phoneLast4: payload.phone?.replace(/\D/g, "").slice(-4),
              errorCode: "rate_limited",
            });
            setErrorMsg(t(
              "Too many attempts. Please wait a minute and try again.",
              "محاولات كثيرة. الرجاء الانتظار دقيقة ثم المحاولة.",
              "Trop de tentatives. Réessayez dans une minute."
            ));
          }
          return null;
        }
        if (res.status === 401) {
          if (!silent) {
            setErrorMsg(t(
              "This tracking link has expired or is invalid. Enter your order number and email instead.",
              "رابط التتبّع منتهي أو غير صالح. أدخل رقم الطلب والبريد بدلاً منه.",
              "Lien de suivi expiré. Entrez le n° de commande et l'email."
            ));
          }
          return null;
        }
        if (res.status >= 500) {
          if (!silent) setErrorMsg(t(
            "Order tracking is temporarily unavailable. Please try again shortly.",
            "تتبّع الطلبات غير متاح مؤقتاً. يرجى المحاولة بعد قليل.",
            "Le suivi est temporairement indisponible. Réessayez dans un instant."
          ));
          return null;
        }
        if (!res.ok) {
          if (!silent) {
            setAttempt({
              orderNumber: payload.orderNumber,
              email: payload.email,
              phoneLast4: payload.phone?.replace(/\D/g, "").slice(-4),
              errorCode: "not_found",
            });
            setErrorMsg(t(
              "We couldn't find an order matching these details.",
              "لم نجد طلباً يطابق هذه البيانات.",
              "Aucune commande trouvée avec ces informations."
            ));
          }
          return null;
        }
        const data = (await res.json()) as TrackResult;
        if (!isCurrent()) return null;
        // تنبيه تلقائي عند تغيّر حالة الطلب بين تحديثين (polling صامت أو يدويّ)
        const prevKey = lastStatusKeyRef.current;
        const nextKey = statusKeyOf(data);
        if (prevKey && prevKey !== nextKey) {
          notifyStatusChange(data, t);
        }
        lastStatusKeyRef.current = nextKey;
        setResult(data);
        setLastFetchedAt(Date.now());
        setLastPayload(payload);
        if (!user && !payload.t) {
          saveLastTrack({
            orderNumber: payload.orderNumber,
            email: payload.email,
            phone: payload.phone,
          });
        }
        return data;
      } catch (err) {
        if (!isCurrent()) return null;
        console.error("[track-order] runTrack failed:", err);
        if (!silent) toast.error(t("Connection error. Try again.", "خطأ اتّصال. حاول لاحقاً.", "Erreur de connexion."));
        return null;
      } finally {
        if (isCurrent()) {
          if (silent) setRefreshing(false); else setLoading(false);
        }
      }
    },
    [t, user, authLoading, setAttempt],

  );

  // Polling تلقائي كلّ ٤٥ث — يتوقّف للحالات النهائيّة وعندما يكون التبويب مخفيّاً
  const pollingEnabled = !!lastPayload && !!result && !FINAL_STATUSES.has(result.fulfillmentStatus);
  usePollingRefresh(
    async () => {
      if (!lastPayload) return;
      await runTrack(lastPayload, { silent: true });
    },
    { intervalMs: 45_000, enabled: pollingEnabled },
  );

  // تتبّع تلقائي من URL search params (من نقرة الإيميل/الواتساب)
  const autoTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoTrackedRef.current) return;
    // انتظر استرجاع الجلسة أولاً — وإلّا رُفضت النتيجة عند عودة الحساب.
    if (authLoading) return;
    // رابط موقَّع: تتبّع فوريّ بدون أيّ نموذج

    if (search.t) {
      autoTrackedRef.current = true;
      setLoading(true);
      void runTrack({ t: search.t });
      return;
    }
    const wantsAuto = search.auto === true || (!!search.order && (!!search.email || !!search.phone));
    const hasIdentifier = !!search.email || !!search.phone || !!user?.email;
    if (!search.order || !wantsAuto || !hasIdentifier) return;
    autoTrackedRef.current = true;
    const payload: { orderNumber: string; email?: string; phone?: string } = {
      orderNumber: search.order.trim(),
    };
    if (search.email) payload.email = search.email.trim();
    else if (search.phone) payload.phone = search.phone.trim();
    else if (user?.email) payload.email = user.email;
    void runTrack(payload);
  }, [authLoading, search.t, search.order, search.email, search.phone, search.auto, user?.email, runTrack]);

  // اقتراح استئناف آخر بحث (للضيوف فقط، ما لم يكن هناك deep-link)
  React.useEffect(() => {
    if (user) return;
    if (search.order || result) return;
    const last = readLastTrack();
    if (last && (last.orderNumber || last.email || last.phone)) {
      setResumeSuggestion(last);
    }
  }, [user, search.order, result]);

  function applyResume() {
    if (!resumeSuggestion) return;
    if (resumeSuggestion.orderNumber) setOrderNumber(resumeSuggestion.orderNumber);
    if (resumeSuggestion.email) setEmail(resumeSuggestion.email);
    setResumeSuggestion(null);
    void runTrack({
      orderNumber: resumeSuggestion.orderNumber,
      email: resumeSuggestion.email,
      phone: resumeSuggestion.phone,
    });
  }

  function dismissResume() {
    setResumeSuggestion(null);
    if (typeof window !== "undefined") {
      try { window.localStorage.removeItem(LAST_TRACK_KEY); } catch { /* ignore */ }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mountedRef.current || accountId !== activeAccountRef.current) return;
    lookupGenerationRef.current++;
    activePayloadRef.current = null;
    setLastPayload(null);
    setLastFetchedAt(null);
    setLoading(false);
    setRefreshing(false);
    setErrorMsg(null);
    setResult(null);
    lastStatusKeyRef.current = null;

    const payload: { orderNumber?: string; email?: string; phone?: string } = {};

    if (orderNumber.trim()) {
      payload.orderNumber = orderNumber.trim();
    }

    if (email.trim()) {
      if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
        setErrorMsg(t("Please enter a valid email.", "البريد المُدخل غير صحيح.", "Email invalide."));
        return;
      }
      payload.email = email.trim();
    }

    // 🔒 للخصوصيّة: نطلب رقم الطلب + البريد معاً.
    // المستخدم الذي لا يملك رقم الطلب يستعمل تبويب «نسيت الرقم» (إيميل + آخر ٤).
    if (!payload.orderNumber || !payload.email) {
      setErrorMsg(t(
        "Please enter your order number AND the email used at checkout. If you don't have the order number, use \"I forgot it\".",
        "الرجاء إدخال رقم الطلب مع البريد المستخدم عند الشراء. إن لم يكن لديك رقم الطلب، استعمل تبويب «نسيت الرقم».",
        "Entrez votre n° de commande ET l'email utilisé. Sans n°, utilisez « J'ai oublié »."
      ));
      return;
    }

    await runTrack(payload);
  }

  // «نسيت رقم الطلب» حُذف: الضيف يتحقّق برقم هاتفه ورمز، فلا حاجة لبريد + آخر ٤.



  // أدمن فتح رابط واتساب من عميل (يحتوي order/email/last4) → حوِّله مباشرة
  // إلى /admin/orders بالبحث جاهزاً بدل إظهار نموذج البحث العامّ.
  const adminLookupQ = (search.order ?? search.email ?? search.last4 ?? "").trim();

  // أدمن فتح رابط واتساب من زائر لم يُدخل أيّ بيانات (ctx=empty)
  // لا نُحوِّل — نُبقي الأدمن على الصفحة مع toast توضيحيّ.
  // رسالة الواتساب نفسها تحوي السياق الكامل.
  const adminEmptyShownRef = React.useRef(false);
  React.useEffect(() => {
    if (adminLoading || !isAdmin) return;
    if (search.ctx !== "empty" || adminLookupQ) return;
    if (adminEmptyShownRef.current) return;
    adminEmptyShownRef.current = true;
    toast.info(
      t(
        "Admin: the visitor opened this page but didn't enter any details.",
        "أدمن: الزائر فتح الصفحة دون إدخال أيّ بيانات.",
        "Admin : le visiteur a ouvert la page sans rien saisir.",
      ),
      { duration: 6000 },
    );
  }, [adminLoading, isAdmin, search.ctx, adminLookupQ, t]);

  // Register every hook before a role-dependent return: adminLoading can
  // change after the first render of a customer's tracking link.
  if (!adminLoading && isAdmin && adminLookupQ) {
    if (typeof window !== "undefined") {
      toast.info(
        t(
          "Admin: opened in orders panel with the customer's search.",
          "أدمن: فُتح بحث الأدمن آلياً ببيانات العميل.",
          "Admin : ouvert dans le panneau commandes avec la recherche du client.",
        ),
      );
    }
    return <Navigate to="/admin/orders" search={{ q: adminLookupQ }} />;
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-2xl px-4 pb-24 pt-8 sm:px-6 md:px-8" dir={dir}>
        <div className="text-center">
          <Package className="mx-auto h-10 w-10 text-primary" />
          <p className="mt-4 t-eyebrow-muted tracking-[var(--tracking-wider)]">
            {t("Order status", "حالة الطلب", "Statut de la commande")}
          </p>
          <h1 className="mt-2 t-h1 text-foreground">
            {t("Track your order", "تتبّع طلبك", "Suivre votre commande")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t(
              "Enter the mobile number used for your order. We’ll send you a verification code.",
              "أدخل رقم الهاتف المستخدم في طلبك، وسنرسل لك رمز تحقق.",
              "Entrez le numéro de mobile utilisé pour votre commande. Nous vous enverrons un code de vérification."
            )}
          </p>
        </div>

        {!user && (
          <p className="mt-6 text-center text-xs text-muted-foreground">
            {t("Have an account?", "هل لديك حساب؟", "Vous avez un compte ?")}{" "}
            <Link to="/auth" search={{ redirect: "/track-order", mode: "signin" }} className="t-link">
              {t("Sign in to see all your orders", "سجّل دخولك لرؤية كلّ طلباتك", "Connectez-vous pour voir vos commandes")}
            </Link>
          </p>
        )}

        {!user && resumeSuggestion && (
          <div className="mt-6 flex flex-col gap-3 rounded-none border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="t-eyebrow-muted">
                {t("Resume your last search", "استئناف آخر بحث", "Reprendre votre dernière recherche")}
              </p>
              <p className="mt-1 truncate text-sm text-foreground" dir="ltr">
                {resumeSuggestion.orderNumber || resumeSuggestion.email || resumeSuggestion.phone}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={applyResume}
                className="rounded-none bg-primary px-4 py-2 t-caption uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition hover:bg-primary/90"
              >
                {t("Resume", "استئناف", "Reprendre")}
              </button>
              <button
                type="button"
                onClick={dismissResume}
                className="rounded-none border border-border px-3 py-2 text-xs text-muted-foreground transition hover:bg-muted/40"
              >
                {t("Dismiss", "إخفاء", "Masquer")}
              </button>
            </div>
          </div>
        )}

        {user && (
          <MyOrdersSection
            orders={myOrders}
            loading={myOrdersLoading}
            hasError={myOrdersError}
            t={t}
            dir={dir}
            locale={locale}
            onSelect={(o) => {
              setOrderNumber(o.orderName);
              if (user.email) setEmail(user.email);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}

        {/* الضيف يتتبّع برقم الهاتف + رمز تحقّق فقط — لا رقم طلب ولا بريد.
            تبويبات «رقم الطلب / نسيته» بقيت للمستخدم المسجَّل وحده. */}
        {!user && <TrackOtpPanel t={t} locale={locale} />}

        {/* الفورم المعتاد (رقم الطلب + البريد) للمستخدم المسجَّل فقط */}
        {user && (

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-2 block t-eyebrow-muted">
              {t("Order number", "رقم الطلب", "Numéro de commande")}
            </label>
            <Input
              placeholder={t("e.g. #1023", "مثال #1023", "ex. #1023")}
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="h-11"
              dir="ltr"
            />
          </div>

          <div>
            <label className="mb-2 block t-eyebrow-muted">
              {t("Email", "البريد الإلكتروني", "Email")}
            </label>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t("Email used at checkout", "البريد المستخدم عند الشراء", "Email du paiement")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
              dir="ltr"
            />
          </div>

          {errorMsg && (
            <div className="flex items-start gap-2 rounded-none border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || authLoading}

            className="flex w-full items-center justify-center gap-2 bg-primary py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading
              ? t("Searching...", "جاري البحث...", "Recherche...")
              : t("Track", "تتبّع", "Suivre")}
          </button>
        </form>
        )}

        {loading && !result && <TrackingResultSkeleton />}

        {result && (
          <TrackingResult
            result={result}
            t={t}
            dir={dir}
            locale={locale}
            lastFetchedAt={lastFetchedAt}
            refreshing={refreshing}
            onRefresh={lastPayload ? () => { void runTrack(lastPayload, { silent: true }); } : undefined}
            pollingActive={pollingEnabled}
          />
        )}
      </div>
    </SiteShell>
  );
}

function TrackingResult({
  result,
  t,
  dir,
  locale,
  lastFetchedAt,
  refreshing,
  onRefresh,
  pollingActive,
}: {
  result: TrackResult;
  t: (en: string, ar: string, fr: string) => string;
  dir: "ltr" | "rtl";
  locale: string;
  lastFetchedAt?: number | null;
  refreshing?: boolean;
  onRefresh?: () => void;
  pollingActive?: boolean;
}) {
  const status = result.fulfillmentStatus;
  const statusInfo = getStatusInfo(status, t);

  const dateLocale = locale === "ar" ? "ar" : locale === "fr" ? "fr-FR" : "en-US";
  const formatDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" }) : "—";

  // Tick كلّ ثانية لتحديث «آخر تحديث: قبل ١٢ث»
  const [, forceTick] = React.useState(0);
  React.useEffect(() => {
    if (!lastFetchedAt) return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [lastFetchedAt]);

  function formatRelative(ms: number): string {
    const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (sec < 60) return t(`${sec}s ago`, `قبل ${sec} ث`, `il y a ${sec}s`);
    const min = Math.floor(sec / 60);
    if (min < 60) return t(`${min} min ago`, `قبل ${min} د`, `il y a ${min} min`);
    const h = Math.floor(min / 60);
    return t(`${h}h ago`, `قبل ${h} س`, `il y a ${h} h`);
  }

  function handleResendWhatsApp() {
    const statusEmoji = getStatusEmoji(result.fulfillmentStatus);
    const tLoc = (en: string, ar: string, fr: string) =>
      locale === "ar" ? ar : locale === "fr" ? fr : en;
    const label = getStatusInfo(result.fulfillmentStatus, tLoc).label;
    const body =
      locale === "ar"
        ? buildArabicMessage(result, label, statusEmoji)
        : locale === "fr"
          ? buildFrenchMessage(result, label, statusEmoji)
          : buildEnglishMessage(result, label, statusEmoji);

    // سطر «آخر تحديث» — يستخدم relative إن توفّر lastFetchedAt، وإلا تاريخ الشحنة
    const updatedSourceMs = lastFetchedAt
      ?? (result.tracking?.updatedAt ? new Date(result.tracking.updatedAt).getTime() : null);
    let updatedLine = "";
    if (updatedSourceMs) {
      const rel = formatRelative(updatedSourceMs);
      const absolute = new Date(updatedSourceMs).toLocaleString(dateLocale, {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      });
      updatedLine = `\n\n🕒 ${t("Last updated", "آخر تحديث", "Dernière mise à jour")}: ${rel} (${absolute})`;
    }

    let deepLink = "";
    if (typeof window !== "undefined") {
      const url = new URL("/track-order", window.location.origin);
      url.searchParams.set("order", result.orderName.replace(/^#/, ""));
      url.searchParams.set("auto", "true");
      deepLink = `\n\n${url.toString()}`;
    }
    const text = encodeURIComponent(body + updatedLine + deepLink);
    const href = `https://wa.me/?text=${text}`;
    if (typeof window !== "undefined") window.open(href, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="mt-12 border border-border" dir={dir}>
      <ThankYouVideoPlayer className="border-0 border-b border-border" />
      <div className="border-b border-border bg-muted/30 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="t-eyebrow-muted">
              {t("Order", "الطلب", "Commande")}
            </p>
            <p className="mt-1 t-h3 t-tabular text-foreground" dir="ltr">{result.orderName}</p>
          </div>
          <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs ${statusInfo.bg}`}>
            <statusInfo.icon className="h-4 w-4" />
            <span>{statusInfo.label}</span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {t("Placed on", "أُنشئ في", "Passé le")} {formatDate(result.createdAt)}
          </p>
          {(lastFetchedAt || onRefresh) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {pollingActive && (
                <span className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  <span>{t("Live", "تحديث تلقائي", "En direct")}</span>
                </span>
              )}
              {lastFetchedAt && (
                <span className="t-tabular" dir="ltr">
                  {formatRelative(lastFetchedAt)}
                </span>
              )}
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  aria-label={t("Refresh now", "تحديث الآن", "Actualiser")}
                  title={t("Refresh now", "تحديث الآن", "Actualiser")}
                  className="rounded-full p-1 text-muted-foreground transition hover:bg-muted/60 hover:text-foreground disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                </button>
              )}
              <button
                type="button"
                onClick={handleResendWhatsApp}
                aria-label={t("Resend tracking link on WhatsApp", "إعادة إرسال رابط التتبّع على واتساب", "Renvoyer le lien de suivi sur WhatsApp")}
                title={t("Resend on WhatsApp", "إعادة إرسال على واتساب", "Renvoyer sur WhatsApp")}
                className="rounded-full p-1 text-emerald-600 transition hover:bg-emerald-500/10 hover:text-emerald-700"
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </button>

            </div>
          )}
        </div>

        {/* الطلبات الملغاة/المسترجَعة لا خطّ زمنيّ لها */}
        {status === "cancelled" || status === "refunded" ? (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive" dir={dir}>
            {status === "cancelled"
              ? t(
                  "This order was cancelled. No shipment will be made.",
                  "هذا الطلب ملغى، ولن يتمّ شحنه.",
                  "Cette commande a été annulée.",
                )
              : t(
                  "This order was refunded.",
                  "هذا الطلب تمّ استرجاع مبلغه.",
                  "Cette commande a été remboursée.",
                )}
          </p>
        ) : (
          /* خطّ زمني مرئي مع تواريخ تقديريّة/فعليّة */
          <StatusTimeline
            status={status}
            localStatus={result.localDelivery?.status ?? null}
            t={t}
            dir={dir}
            locale={locale}
            createdAt={result.createdAt}
            shippedAt={result.localDelivery?.pickedUpAt ?? result.tracking?.createdAt ?? null}
            deliveredAt={result.localDelivery?.deliveredAt ?? null}
          />
        )}
      </div>

      {/* التسليم المحليّ بسائقنا — يظهر فقط إن وُجد سجلّ محليّ والميزة مفعَّلة */}
      {(() => {
        const ld = result.localDelivery;
        if (!ld) return null;
        const info = localDeliveryLabel(ld.status, t);
        if (!info) return null;
        const stamp = ld.deliveredAt ?? ld.pickedUpAt ?? ld.assignedAt;
        return (
          <div className="border-b border-border p-5">
            <p className="t-eyebrow-muted">
              {t("Local delivery", "التوصيل المحليّ", "Livraison locale")}
            </p>
            <p className="mt-2 text-sm text-foreground">
              {info.emoji} {info.label}
            </p>
            <p className="mt-1 t-eyebrow normal-case tracking-normal text-muted-foreground">
              {info.hint}
            </p>
            {ld.driverName && (
              <p className="mt-2 t-eyebrow normal-case tracking-normal text-muted-foreground">
                {t("Driver", "السائق", "Livreur")}: {ld.driverName}
              </p>
            )}
            {stamp && (
              <p className="mt-2 t-eyebrow normal-case tracking-normal text-muted-foreground">
                {t("Last update", "آخر تحديث", "Dernière mise à jour")}: {formatDate(stamp)}
              </p>
            )}
          </div>
        );
      })()}

      {result.tracking && (result.tracking.number || result.tracking.url) && (
        <div className="border-b border-border p-5">
          <p className="t-eyebrow-muted">
            {t("Shipment", "الشحنة", "Expédition")}
          </p>
          {result.tracking.company && (
            <p className="mt-2 text-sm text-foreground">{result.tracking.company}</p>
          )}
          {result.tracking.number && (
            <p className="mt-1 font-mono text-sm text-foreground" dir="ltr">
              {result.tracking.number}
            </p>
          )}
          {result.tracking.url && (
            <a
              href={result.tracking.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block t-caption uppercase tracking-[var(--tracking-wider)] text-primary underline"
            >
              {t("Track on carrier", "تتبّع لدى الناقل", "Suivre chez le transporteur")} →
            </a>
          )}
          {result.tracking.updatedAt && (
            <p className="mt-3 t-eyebrow normal-case tracking-normal text-muted-foreground">
              {t("Last update", "آخر تحديث", "Dernière mise à jour")}: {formatDate(result.tracking.updatedAt)}
            </p>
          )}
        </div>
      )}

      <div className="p-5">
        <p className="t-eyebrow-muted">
          {t("Items", "المنتجات", "Articles")}
        </p>
        <ul className="mt-3 space-y-3">
          {result.items.map((item, i) => (
            <li key={i} className="flex items-center gap-3 text-sm text-foreground">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-none border border-border bg-muted/40">
                {item.image ? (
                  <img
                    src={item.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Package className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <span className="min-w-0 flex-1 truncate">{item.title}</span>
              <span className="shrink-0 text-muted-foreground">× {item.qty}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-border p-5">
        <MessagePreviewAndShare result={result} t={t} initialLocale={locale as "ar" | "en" | "fr"} />
      </div>

      <TrackOrderActions result={result} t={t} locale={locale} />
    </div>
  );
}

/** أزرار إجراء أسفل النتيجة: تسوّق · دعم واتساب · إعادة الطلب بعد التسليم. */
function TrackOrderActions({
  result,
  t,
  locale,
}: {
  result: TrackResult;
  t: (en: string, ar: string, fr: string) => string;
  locale: string;
}) {
  const status = result.fulfillmentStatus;
  const delivered = status === "delivered" || result.localDelivery?.status === "delivered";
  const finalStage = delivered || FINAL_STATUSES.has(status);

  // تنبيه المتصفّح — اختياريّ، يُطلب بنقرة المستخدم فقط
  const [notifyState, setNotifyState] = React.useState<NotificationPermission | "unsupported">("default");
  React.useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setNotifyState("unsupported");
      return;
    }
    setNotifyState(Notification.permission);
  }, []);

  async function enableNotifications() {
    try {
      const perm = await Notification.requestPermission();
      setNotifyState(perm);
      if (perm === "granted") {
        toast.success(
          t("Alerts on", "التنبيهات مفعّلة", "Alertes activées"),
          {
            description: t(
              "We'll notify you here when this order moves.",
              "سننبّهك هنا فور تغيّر حالة طلبك.",
              "Nous vous alerterons ici à chaque étape.",
            ),
          },
        );
      } else {
        toast.error(t("Alerts blocked", "التنبيهات محظورة", "Alertes bloquées"));
      }
    } catch {
      setNotifyState("denied");
    }
  }

  const supportText =
    locale === "ar"
      ? `مرحباً، لديّ استفسار عن الطلب ${result.orderName}`
      : locale === "fr"
        ? `Bonjour, j'ai une question sur la commande ${result.orderName}`
        : `Hello, I have a question about order ${result.orderName}`;
  const supportHref = `https://wa.me/${LECMO_WA_NUMBER}?text=${encodeURIComponent(supportText)}`;

  return (
    <div className="grid gap-2 border-t border-border p-5 sm:grid-cols-2">
      <Link
        to="/shop"
        className="flex items-center justify-center gap-2 border border-border py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition hover:bg-muted/40"
      >
        <ShoppingBag className="h-4 w-4" />
        {t("Continue shopping", "تابع التسوّق", "Continuer vos achats")}
      </Link>

      <a
        href={supportHref}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 border border-border py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition hover:bg-muted/40"
      >
        <MessageCircle className="h-4 w-4 text-emerald-600" />
        {t("Contact us", "تواصل معنا", "Nous contacter")}
      </a>

      {!finalStage && notifyState === "default" && (
        <button
          type="button"
          onClick={enableNotifications}
          className="flex items-center justify-center gap-2 border border-border py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-foreground transition hover:bg-muted/40 sm:col-span-2"
        >
          <Bell className="h-4 w-4" />
          {t("Alert me on updates", "نبّهني عند التحديث", "M'alerter des mises à jour")}
        </button>
      )}

      {!finalStage && notifyState === "granted" && (
        <p className="flex items-center justify-center gap-2 py-2 t-caption text-muted-foreground sm:col-span-2">
          <BellRing className="h-4 w-4 text-emerald-600" />
          {t("Alerts are on for this order.", "التنبيهات مفعّلة لهذا الطلب.", "Alertes activées pour cette commande.")}
        </p>
      )}

      {delivered && (
        <Link
          to="/shop"
          className="flex items-center justify-center gap-2 bg-primary py-3 t-caption uppercase tracking-[var(--tracking-wider)] text-primary-foreground transition hover:bg-primary/90 sm:col-span-2"
        >
          <RotateCcw className="h-4 w-4" />
          {t("Order again", "أعد الطلب", "Commander à nouveau")}
        </Link>
      )}
    </div>
  );
}

/** هيكل تحميل بشكل بطاقة النتيجة — يمنع قفز التخطيط أثناء البحث. */
function TrackingResultSkeleton() {
  return (
    <div className="mt-12 animate-pulse border border-border" aria-hidden="true">
      <div className="border-b border-border bg-muted/30 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-2">
            <div className="h-3 w-16 bg-muted" />
            <div className="h-5 w-28 bg-muted" />
          </div>
          <div className="h-7 w-24 rounded-full bg-muted" />
        </div>
        <div className="mt-4 h-3 w-40 bg-muted" />
        <div className="mt-5 flex items-center gap-2">
          {[0, 1, 2, 3].map((i) => (
            <React.Fragment key={i}>
              <div className="h-6 w-6 shrink-0 rounded-full bg-muted" />
              {i < 3 && <div className="h-0.5 flex-1 bg-muted" />}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="p-5">
        <div className="h-3 w-20 bg-muted" />
        <div className="mt-4 space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-12 w-12 shrink-0 bg-muted" />
              <div className="h-4 flex-1 bg-muted" />
              <div className="h-4 w-8 bg-muted" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// مكوّن المعاينة: تبويبات للغات الثلاث + محرّر فقاعة واتساب + زرّ المشاركة
function MessagePreviewAndShare({
  result,
  t,
  initialLocale,
}: {
  result: TrackResult;
  t: (en: string, ar: string, fr: string) => string;
  initialLocale: "ar" | "en" | "fr";
}) {
  const [previewLocale, setPreviewLocale] = React.useState<"ar" | "en" | "fr">(
    initialLocale === "ar" ? "ar" : "en",
  );

  const statusEmoji = getStatusEmoji(result.fulfillmentStatus);

  function statusLabelForLocale(loc: "ar" | "en" | "fr") {
    const tLoc = (en: string, ar: string, fr: string) =>
      loc === "ar" ? ar : loc === "fr" ? fr : en;
    return getStatusInfo(result.fulfillmentStatus, tLoc).label;
  }

  function buildTemplate(loc: "ar" | "en" | "fr") {
    const label = statusLabelForLocale(loc);
    if (loc === "ar") return buildArabicMessage(result, label, statusEmoji);
    if (loc === "fr") return buildFrenchMessage(result, label, statusEmoji);
    return buildEnglishMessage(result, label, statusEmoji);
  }

  // تخزين تعديلات العميل المنفصلة لكلّ لغة (تبقى محفوظة عند تبديل التبويب)
  const [drafts, setDrafts] = React.useState<Record<"ar" | "en" | "fr", string>>(() => ({
    ar: buildArabicMessage(result, statusLabelForLocale("ar"), statusEmoji),
    en: buildEnglishMessage(result, statusLabelForLocale("en"), statusEmoji),
    fr: buildFrenchMessage(result, statusLabelForLocale("fr"), statusEmoji),
  }));

  const currentMessage = drafts[previewLocale];
  const template = buildTemplate(previewLocale);
  const isEdited = currentMessage !== template;

  const handleChange = (val: string) => {
    setDrafts((prev) => ({ ...prev, [previewLocale]: val }));
  };

  const handleReset = () => {
    setDrafts((prev) => ({ ...prev, [previewLocale]: template }));
  };

  const previewDir = previewLocale === "ar" ? "rtl" : "ltr";

  const [copied, setCopied] = React.useState<string | null>(null);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyText = React.useCallback(
    async (text: string, key: string) => {
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopied(key);
        toast.success(t("Copied to clipboard", "تمّ النسخ", "Copié"));
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(null), 2000);
      } catch {
        toast.error(t("Copy failed", "فشل النسخ", "Échec de la copie"));
      }
    },
    [t],
  );

  React.useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const charLimit = 1000;
  const overLimit = currentMessage.length > charLimit;
  const trackingUrl = result.tracking?.url ?? null;
  const showLinks = Boolean(trackingUrl) || Boolean(result.orderName);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between gap-2">
          <p className="t-eyebrow-muted">
            {t("Edit & preview", "حرّر وعاين", "Modifier & aperçu")}
          </p>
          {isEdited && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 t-micro uppercase tracking-[var(--tracking-wide)] text-muted-foreground transition hover:text-foreground"
              aria-label={t("Reset to template", "إعادة تعيين", "Réinitialiser")}
            >
              <RotateCcw className="h-3 w-3" />
              {t("Reset", "إعادة تعيين", "Réinitialiser")}
            </button>
          )}
        </div>

        <Tabs
          value={previewLocale === "fr" ? "en" : previewLocale}
          onValueChange={(v) => setPreviewLocale(v as "ar" | "en" | "fr")}
          className="mt-2"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="ar">العربية</TabsTrigger>
            <TabsTrigger value="en">English</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* محرّر بشكل فقاعة واتساب */}
        <div className="mt-3 rounded-none bg-[#ece5dd] p-3 dark:bg-muted/40">
          <div className="relative rounded-none bg-[#dcf8c6] px-3 py-2  dark:bg-emerald-900/40">
            <button
              type="button"
              onClick={() => copyText(currentMessage, "message")}
              className="absolute end-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-none bg-white/80 text-neutral-700  transition hover:bg-white dark:bg-emerald-950/60 dark:text-emerald-100 dark:hover:bg-emerald-950"
              aria-label={t("Copy message", "نسخ الرسالة", "Copier le message")}
              title={t("Copy message", "نسخ الرسالة", "Copier le message")}
            >
              {copied === "message" ? (
                <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
            <textarea
              key={previewLocale}
              dir={previewDir}
              value={currentMessage}
              onChange={(e) => handleChange(e.target.value)}
              rows={Math.min(Math.max(currentMessage.split("\n").length, 6), 16)}
              className="w-full resize-y border-0 bg-transparent p-0 pe-9 t-body-sm text-neutral-900 outline-none transition-opacity duration-200 focus:ring-0 dark:text-emerald-50"
              spellCheck={false}
              aria-label={t("Edit message", "تعديل الرسالة", "Modifier le message")}
            />
          </div>
          <div className="mt-2 flex items-center justify-between t-micro text-muted-foreground">
            <span>
              {isEdited
                ? t("Edited", "تمّ التعديل", "Modifié")
                : t("Default template", "القالب الأصلي", "Modèle par défaut")}
            </span>
            <span className={overLimit ? "text-amber-600 dark:text-amber-400" : ""}>
              {currentMessage.length}
              {overLimit ? ` / ${charLimit}` : ""} {t("chars", "حرف", "car.")}
            </span>
          </div>
        </div>

        {showLinks && (
          <div className="mt-3">
            <p className="t-eyebrow-muted">
              {t("Links in message", "روابط في الرسالة", "Liens dans le message")}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {trackingUrl && (
                <div className="flex items-center gap-1 rounded-full border border-border bg-muted/40 py-1 ps-3 pe-1 text-xs">
                  <span className="max-w-[180px] truncate font-mono t-eyebrow normal-case tracking-normal text-foreground" dir="ltr">
                    🔗 {trackingUrl.replace(/^https?:\/\//, "")}
                  </span>
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
                    aria-label={t("Open link", "فتح الرابط", "Ouvrir le lien")}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <button
                    type="button"
                    onClick={() => copyText(trackingUrl, "track")}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
                    aria-label={t("Copy link", "نسخ الرابط", "Copier le lien")}
                  >
                    {copied === "track" ? (
                      <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                </div>
              )}
              {result.orderName && (
                <div className="flex items-center gap-1 rounded-full border border-border bg-muted/40 py-1 ps-3 pe-1 text-xs">
                  <span className="font-mono t-eyebrow normal-case tracking-normal text-foreground" dir="ltr">
                    📦 {result.orderName}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyText(result.orderName, "order")}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
                    aria-label={t("Copy order number", "نسخ رقم الطلب", "Copier le numéro")}
                  >
                    {copied === "order" ? (
                      <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <WhatsAppShareButton
        result={result}
        message={currentMessage}
        t={t}
      />
    </div>
  );
}

// دوالّ بناء رسائل واتساب مستخرجة إلى src/lib/whatsappMessages.ts
// لتبقى قابلة للاختبار وحدويّاً ولا تتداخل مع TanStack code-splitting.


function WhatsAppShareButton({
  result,
  message,
  t,
}: {
  result: TrackResult;
  message: string;
  t: (en: string, ar: string, fr: string) => string;
}) {
  const handleShare = async () => {
    const shareData = {
      title: `Lecmo — ${result.orderName}`,
      text: message,
    };
    const encoded = encodeURIComponent(message);
    const waUrl = `https://wa.me/?text=${encoded}`;

    // Web Share API (sheet أصليّ — موبايل غالباً) — يحتاج user gesture + permission
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // إلغاء المستخدم — لا fallback
        if ((err as Error).name === "AbortError") return;
        // أيّ خطأ آخر (NotAllowedError على الديسكتوب) → نسقط للـfallback
      }
    }

    // Fallback: نسخ الرابط للحافظة + فتح واتساب — يعمل دائماً
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(message);
      }
    } catch {
      // ignore — clipboard قد يفشل بدون https
    }
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={!message.trim()}
      className="flex w-full items-center justify-center gap-2 rounded-none bg-[#25D366] py-3 t-caption font-medium uppercase tracking-[var(--tracking-wider)] text-white transition hover:bg-[#1ebe5a] disabled:opacity-50"
      aria-label={t("Share on WhatsApp", "مشاركة عبر واتساب", "Partager sur WhatsApp")}
    >
      <Share2 className="h-4 w-4" />
      {t("Share on WhatsApp", "مشاركة عبر واتساب", "Partager sur WhatsApp")}
    </button>
  );
}

function getStatusInfo(
  status: string,
  t: (en: string, ar: string, fr: string) => string,
) {
  switch (status) {
    case "cancelled":
      return {
        icon: XCircle,
        label: t("Cancelled", "ملغى", "Annulée"),
        bg: "bg-destructive/10 text-destructive",
      };
    case "refunded":
      return {
        icon: XCircle,
        label: t("Refunded", "مُسترجَع", "Remboursée"),
        bg: "bg-destructive/10 text-destructive",
      };
    case "delivered":
      return {
        icon: CheckCircle2,
        label: t("Delivered", "تمّ التسليم", "Livrée"),
        bg: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
      };
    case "fulfilled":
      return {
        icon: Truck,
        label: t("Shipped", "تمّ الشحن", "Expédiée"),
        bg: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
      };
    case "partial":
      return {
        icon: Truck,
        label: t("Partially shipped", "شحن جزئي", "Partiellement expédiée"),
        bg: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
      };
    case "in_progress":
    case "pending_fulfillment":
      return {
        icon: Truck,
        label: t("In transit", "في الطريق", "En transit"),
        bg: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
      };
    default:
      return {
        icon: Clock,
        label: t("Preparing", "قيد التجهيز", "En préparation"),
        bg: "bg-muted text-muted-foreground",
      };
  }
}

function MyOrdersSection({
  orders,
  loading,
  hasError,
  t,
  dir,
  locale,
  onSelect,
}: {
  orders: MyOrderSummary[] | null;
  loading: boolean;
  hasError?: boolean;
  t: (en: string, ar: string, fr: string) => string;
  dir: "ltr" | "rtl";
  locale: string;
  onSelect: (o: MyOrderSummary) => void;
}) {
  const dateLocale = locale === "ar" ? "ar" : locale === "fr" ? "fr-FR" : "en-US";
  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" });

  return (
    <section className="mt-10 border border-border" dir={dir}>
      <header className="border-b border-border bg-muted/30 px-5 py-4">
        <p className="t-eyebrow-muted">
          {t("Your account", "حسابك", "Votre compte")}
        </p>
        <h2 className="mt-1 t-h3 text-foreground">
          {t("Recent orders", "آخر طلباتك", "Commandes récentes")}
        </h2>
      </header>

      {loading && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {!loading && hasError && (
        <div className="flex items-start gap-2 px-5 py-6 text-sm text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            {t(
              "We couldn't load your recent orders right now. You can still track an order using the form below.",
              "تعذّر تحميل طلباتك الحديثة الآن. يمكنك التتبّع يدوياً عبر النموذج أدناه.",
              "Impossible de charger vos commandes récentes pour l'instant. Vous pouvez suivre via le formulaire ci-dessous."
            )}
          </span>
        </div>
      )}

      {!loading && !hasError && orders && orders.length === 0 && (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">
          {t(
            "No recent orders found for your account.",
            "لا توجد طلبات حديثة على حسابك.",
            "Aucune commande récente sur votre compte."
          )}
        </p>
      )}


      {!loading && orders && orders.length > 0 && (
        <ul className="divide-y divide-border">
          {orders.map((o) => {
            const status = getStatusInfo(o.fulfillmentStatus, t);
            return (
              <li key={o.orderName}>
                <button
                  type="button"
                  onClick={() => onSelect(o)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-start transition hover:bg-muted/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="t-mono-sm t-tabular text-foreground" dir="ltr">{o.orderName}</span>
                      <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 t-micro ${status.bg}`}>
                        <status.icon className="h-3 w-3" />
                        {status.label}
                      </span>
                    </div>
                    <p className="mt-1 t-eyebrow normal-case tracking-normal text-muted-foreground">
                      {formatDate(o.createdAt)} · {o.itemsCount} {t("items", "منتج", "articles")}
                      {o.tracking?.number && (
                        <span className="ms-2" dir="ltr">
                          · {o.tracking.number}
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground ${dir === "rtl" ? "rotate-180" : ""}`} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// خطّ زمني مرئي بأربع مراحل: الطلب → التجهيز → في الطريق → التسليم
function StatusTimeline({
  status,
  localStatus,
  t,
  dir,
  locale,
  createdAt,
  shippedAt,
  deliveredAt,
}: {
  status: string;
  /** حالة سائقنا المحليّ — لها الأولويّة على حالة Shopify في الشريط */
  localStatus?: string | null;
  t: (en: string, ar: string, fr: string) => string;
  dir: "ltr" | "rtl";
  locale: string;
  createdAt: string;
  shippedAt: string | null;
  deliveredAt: string | null;
}) {
  // تحديد المرحلة الحاليّة (0-3) — التوصيل المحليّ مرجع أعلى من Shopify
  const localStage = (() => {
    switch (localStatus) {
      case "delivered":
        return 3;
      case "out_for_delivery":
      case "picked_up":
        return 2;
      case "assigned":
        return 1;
      default:
        return null;
    }
  })();

  const shopifyStage = (() => {
    switch (status) {
      case "cancelled":
      case "refunded":
        return 0;
      case "delivered":
        return 3;
      case "fulfilled":
      case "partial":
      case "in_progress":
      case "pending_fulfillment":
        return 2;
      case "unfulfilled":
      default:
        return 1; // قيد التجهيز
    }
  })();

  const currentStage =
    localStage !== null ? Math.max(localStage, shopifyStage) : shopifyStage;

  // Status changes do not establish event times. Display only timestamps
  // supplied by the order, fulfillment creation or local delivery record.
  const eventDate = (value: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const localeCode = locale === "ar" ? "ar-EG" : locale === "fr" ? "fr-FR" : "en-GB";
  const fmtDate = (d: Date) =>
    d.toLocaleDateString(localeCode, { day: "numeric", month: "short" });

  const stageDates = [
    eventDate(createdAt),
    null,
    eventDate(shippedAt),
    eventDate(deliveredAt),
  ];

  const stages = [
    { icon: ShoppingBag, label: t("Ordered", "تمّ الطلب", "Commandée") },
    { icon: Clock, label: t("Processing", "قيد التجهيز", "Préparation") },
    { icon: Truck, label: t("In transit", "في الطريق", "En transit") },
    { icon: PackageCheck, label: t("Delivered", "تمّ التسليم", "Livrée") },
  ];

  return (
    <div className="mt-5" dir={dir}>
      <div className="flex items-start justify-between gap-1">
        {stages.map((s, i) => {
          const isDone = i < currentStage;
          const isActive = i === currentStage;
          const isFuture = i > currentStage;
          const sd = stageDates[i];
          const showDate = isDone || isActive;
          return (
            <React.Fragment key={i}>
              <div className="flex flex-1 flex-col items-center gap-1.5 text-center">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full transition ${
                    isDone
                      ? "bg-emerald-500 text-white"
                      : isActive
                      ? "bg-primary text-primary-foreground ring-4 ring-primary/15"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isDone ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <s.icon className="h-4 w-4" />
                  )}
                </div>
                <span
                  className={`t-micro leading-tight ${
                    isFuture ? "text-muted-foreground" : "font-medium text-foreground"
                  }`}
                >
                  {s.label}
                </span>
                {showDate && sd && (
                  <span
                    className="text-[10px] leading-tight text-foreground/70"
                  >
                    {fmtDate(sd)}
                  </span>
                )}
              </div>
              {i < stages.length - 1 && (
                <div
                  className={`mt-4 h-0.5 flex-1 transition ${
                    i < currentStage ? "bg-emerald-500" : "bg-border"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {stageDates.some((date, index) => index <= currentStage && !date) && (
        <p className="mt-3 text-center text-[11px] text-muted-foreground italic">
          {t(
            "Dates appear when confirmed updates are available.",
            "تظهر التواريخ عند توفّر تحديثات مؤكّدة.",
            "Les dates s’affichent lorsque des mises à jour confirmées sont disponibles.",
          )}
        </p>
      )}
    </div>
  );
}
